// Apolo loop — todo-discipline (E11-WS1-T03). PURE (no React/IPC). The Cursor/Claude
// todo rule, made executable: a todo list is for PLAN-ALTITUDE work, not bookkeeping.
//   1. Only use todos for a 3+ step task (a 1-2 step task is just done inline).
//   2. Exactly one step is "active" (in_progress) at a time.
//   3. lint / test / search / grep / read are SUB-steps, not todos (they happen within a
//      step; tracking them as todos is noise that buries the actual plan altitude).
//
// This complements planState.js (the state machine) — it does not replace it. Use
// `isAtTaskAltitude`/`shouldUseTodos` BEFORE building todos, and `normalizeToDiscipline`
// to coerce a model-proposed list into the discipline (demote extra actives, drop sub-steps).

// Sub-task-altitude verbs/commands that should NOT be their own todo: lint/test/build/
// search/read invocations — whether bare ("lint"), Portuguese-verbed ("rodar lint",
// "buscar X"), or command-shaped ("npm test", "vitest run").
const SUBSTEP = /^\s*(?:(?:rod(?:ar|e)|run)\s+)?(?:npm|yarn|pnpm|vitest|jest|eslint|tsc|lint|test(?:e|ar|s)?|typecheck|build|grep|grep_search|search|semantic_search|busca(?:r)?|procura(?:r)?|list_files|listar|read_file)\b|^\s*ler\s+(?:o\s+)?arquivo\b/i;

/** Is this step at TASK altitude (a real plan step), not a sub-step (lint/test/search/read)? */
export function isAtTaskAltitude(text) {
  const t = String(text || '').trim();
  if (!t) return false;
  return !SUBSTEP.test(t);
}

/** The plan-altitude steps only (sub-steps filtered out). */
export function taskAltitudeSteps(steps) {
  return (Array.isArray(steps) ? steps : [])
    .map((s) => (typeof s === 'string' ? s : s?.text))
    .filter((t) => typeof t === 'string' && isAtTaskAltitude(t));
}

/** Should this task use a todo list at all? Only when it has 3+ plan-altitude steps. */
export function shouldUseTodos(steps) {
  return taskAltitudeSteps(steps).length >= 3;
}

/**
 * Validate a todo list against the discipline.
 * @param {Array<{text?:string,status?:string}>} todos
 * @returns {{valid:boolean, violations:string[]}}
 */
export function validateTodoDiscipline(todos) {
  const list = Array.isArray(todos) ? todos : [];
  const violations = [];
  const actives = list.filter((t) => t?.status === 'active');
  if (actives.length > 1) violations.push(`more_than_one_active:${actives.length}`);
  const subSteps = list.filter((t) => t && typeof t.text === 'string' && !isAtTaskAltitude(t.text));
  if (subSteps.length) violations.push(`sub_step_as_todo:${subSteps.length}`);
  return { valid: violations.length === 0, violations };
}

/**
 * Enforce discipline rule #2 — EXACTLY ONE active — without dropping any step
 * (unlike normalizeToDiscipline's sub-step filter, this is domain-agnostic: safe for
 * research plans where "search" IS the work). Keeps the FIRST active; demotes the rest
 * to pending. `preferId` (optional) forces THAT id to be the kept active (the todo the
 * agent just set active), demoting all others — used by the planState reducer.
 * harness-audit 07-13 — wires the (previously dead) todoDiscipline into the live reducer.
 * Generic passthrough (T→T) so callers keep their exact element type (e.g. planState's Todo).
 * @template {{id?:string,status?:string}} T
 * @param {T[]} todos
 * @param {string|null} [preferId]
 * @returns {T[]}
 */
export function demoteExtraActives(todos, preferId = null) {
  const list = Array.isArray(todos) ? todos : [];
  const actives = list.filter((t) => t?.status === 'active');
  if (actives.length <= 1) return list; // already disciplined — same identity, zero churn
  let kept = false;
  return list.map((t) => {
    if (t?.status !== 'active') return t;
    const isKeeper = preferId ? t.id === preferId : !kept;
    if (isKeeper) { kept = true; return t; }
    return /** @type {T} */ ({ ...t, status: 'pending' });
  });
}

/**
 * Coerce a model-proposed todo list into the discipline (non-destructive shape):
 *   - drop sub-step todos (lint/test/search/read);
 *   - keep only the FIRST active, demote the rest to pending.
 * Returns the same array shape with offending items removed/adjusted.
 * @param {Array<{id?:string,text?:string,status?:string}>} todos
 */
export function normalizeToDiscipline(todos) {
  const list = Array.isArray(todos) ? todos : [];
  let sawActive = false;
  return list
    .filter((t) => t && typeof t.text === 'string' && isAtTaskAltitude(t.text))
    .map((t) => {
      if (t.status === 'active') {
        if (sawActive) return { ...t, status: 'pending' };
        sawActive = true;
      }
      return t;
    });
}
